/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import cn from "classnames";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import VerificationInput from "react-verification-input";

import anonymizeEmail from "utils/anonymizeEmail";
import openToast from "utils/openToast";

import { emailVerificationCode } from "lib/v3/forgetPasswordApi";

import { GoBackIcon } from "components/icons";

import styles from "../style.module.css";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import Label from "components/ui/Label";
import { Button } from "components/ui/Button";

interface EmailVerificationProps {
  setCurrentForm: (formName: string) => void;
  email: string;
}

interface VerificationFormData {
  verificationCode: string;
}

const EmailVerification = ({
  setCurrentForm,
  email,
}: EmailVerificationProps) => {
  const [timer, setTimer] = useState(90);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<VerificationFormData>({
    defaultValues: {
      verificationCode: "",
    },
  });

  const handleComplete = () => {
    const verificationCode = getValues("verificationCode");
    if (verificationCode.length === confirmationLength) {
      handleSubmit(onSubmit)();
    }
  };

  const verifyCode = useMutation(emailVerificationCode);
  const confirmationCode = watch("verificationCode");
  const confirmationLength = 6;
  const isCompleted = confirmationCode?.length === confirmationLength;
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  const onSubmit: SubmitHandler<any> = () => {
    setIsLoading(true);
    const code = getValues("verificationCode");
    const data = { code, email };
    verifyCode.mutate(data, {
      onSuccess: ({ data }) => {
        setIsLoading(false);
        const { token, step } = data;
        localStorage.setItem("token", token);
        setCurrentForm(step);
      },
      onError: () => {
        setIsLoading(false);

        setError("verificationCode", {
          type: "manual",
          message: "The verification code that you entered is incorrect",
        });
      },
    });
  };

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((timer) => timer - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const onResendClick = () => {
    reset();
    setIsResendDisabled(true);
    setTimer(90);
    openToast("A new verification code has been sent", "success", 2000);
  };

  const userEmail = anonymizeEmail(email || "");
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Stack className="space-y-1 mb-8">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentForm("forget_password")}
            >
              <GoBackIcon fill="#fff" width="20" height="20" />
            </button>

            <h3 className="text-white text-xl font-bold">Email Verification</h3>
          </div>

          <Text className="text-sm text-[#9A9A9A]">
            If <span className="text-[#fff]">‘{userEmail}’</span> is registered,
            you will receive a verification code. Check your inbox and enter the
            code below to proceed.
          </Text>
        </Stack>

        <Stack
          className={cn("space-y-2 mb-12", { "mb-8": errors.verificationCode })}
        >
          <Label
            htmlFor="verificationCode"
            variant={"dark"}
            isBold={false}
            className="text-lg font-bold text-[#BABABA]"
          >
            Verification Code
          </Label>

          <div className="flex justify-center">
            <Controller
              name="verificationCode"
              control={control}
              render={({ field }) => (
                <VerificationInput
                  {...field}
                  placeholder="-"
                  length={confirmationLength}
                  onChange={(value) => field.onChange(value)}
                  onComplete={handleComplete}
                  classNames={{
                    container: "w-[350px]",
                    characterSelected: cn(
                      "border border-solid border-[#535353] bg-transparent text-s align-middle text-center outline-none	text-white",
                      errors.verificationCode
                        ? "border-[#E46D64] text-white"
                        : "border-[#535353] text-white",
                    ),
                    characterInactive: cn(
                      "border border-solid border-[#535353] bg-transparent text-s align-middle text-center",
                      errors.verificationCode
                        ? "border-[#E46D64] text-white"
                        : "border-[#535353] text-white",
                    ),
                    characterFilled: cn(
                      "border border-solid border-[#535353] bg-transparent align-middle text-center text-white",
                      errors.verificationCode
                        ? "border-[#E46D64] text-white"
                        : "border-[#535353] text-white",
                    ),
                    character: cn(
                      "bg-transparent border border-solid border-[#535353] text-[#535353]",
                      errors.verificationCode
                        ? "border-[#E46D64] text-white"
                        : "border-[#535353] text-white",
                    ),
                  }}
                />
              )}
            />
          </div>
        </Stack>

        {errors.verificationCode && (
          <Text className="mb-5 text-xs text-[#F55B56]">
            {errors.verificationCode.message}
          </Text>
        )}

        <Stack className="space-y-5">
          <Button
            variant="primary"
            type="submit"
            size="xl"
            shouldPreventDefault={false}
            isLoading={isLoading}
            disabled={!isCompleted || verifyCode.isLoading}
            className={cn({
              [styles.disabled]: !isCompleted || verifyCode.isLoading,
            })}
            id="loginBtn"
          >
            Verify Email
          </Button>

          <div className="flex justify-center items-center ">
            <Text
              className={cn("text-xs font-bold text-white py-2 px-3", {
                "!text-[#535353]": timer === 0,
              })}
            >
              {timer}
            </Text>

            <Button
              variant="pg-header-secondary"
              disabled={isResendDisabled || verifyCode.isLoading}
              className={cn(
                " py-2 px-3 h-[32px]  w-[86px] font-bold text-xs bg-transparent border-none text-nowrap",
                {
                  [styles.disabled]: isResendDisabled || verifyCode.isLoading,
                },
              )}
              onClick={onResendClick}
            >
              Resend Code
            </Button>
          </div>
        </Stack>
      </Stack>
    </form>
  );
};

export default EmailVerification;
